// Per-form permission model (migration 20260922120000_forms_per_form_permission_model.sql):
// FormRoles rows carry three grants per (formID, roleID):
//   canView           → "Submit Entries": see the published form + submit entries
//   canEdit           → "Edit": edit/delete/archive/share the form + read entries/files
//   canManageEntries  → "Manage Entries": view/update/delete the form's entries
// The global 'View Forms' / 'Edit Forms' / 'Delete Forms' / 'Manage Form Entries'
// permissions no longer exist — only 'Create Forms' remains global.

const roleIDsForUser = (user) => (user?.Roles || user?.roles || []).map((role) => role.id || role.roleID).filter(Boolean);

const hasMatchingRoleGrant = (form, user, flag) => {
    const userRoleIDs = roleIDsForUser(user);
    return (form?.FormRoles || []).some((role) => role[flag] && userRoleIDs.includes(role.roleID));
};

// "Edit" grant: edit/delete/archive the form and manage its FormRoles sharing.
// Includes read-only entry/file access. Entry mutations still require Manage Entries.
export const canEditForm = (form, user) => {
    if (!form || !user) return false;
    return user.isAdmin || user.id === form.createdBy || hasMatchingRoleGrant(form, user, 'canEdit');
};

// Deleting/archiving a form is covered by the same "Edit" grant.
export const canDeleteForm = (form, user) => canEditForm(form, user);

// "Submit Entries" (canView): see the published form and submit entries.
// Mirrors the submit-form edge function: status must be 'published', then
// allowed iff the form isPublic OR caller is admin/creator OR belongs to a
// role with canView on the form. (Admin/creator bypass the canView check.)
export const canFillForm = (form, user) => {
    if (!form || !user || form.status !== 'published') return false;
    if (form.isPublic) return true;
    return user.isAdmin || user.id === form.createdBy || hasMatchingRoleGrant(form, user, 'canView');
};

// "Manage Entries" (canManageEntries): view/update/delete the form's entries.
// No form-edit access. Admin and creator always qualify.
// NOTE: an empty FormRoles list means restricted — do NOT treat it as
// unrestricted; the embed is reliably populated under the widened RLS.
export const canManageFormEntries = (form, user) => {
    if (!form || !user) return false;
    return user.isAdmin || user.id === form.createdBy || hasMatchingRoleGrant(form, user, 'canManageEntries');
};

export const formVisibilityLabel = (form) => form.isPublic ? 'Public' : (form.FormRoles?.length ? 'Role-scoped' : 'Private');
